import { ChatbotConfig } from '../config/chatbot-config';
import Cookies from 'js-cookie';

const STATUS_BY_EVENT = {
  'response.created': 'Thinking...',
  'response.file_search_call.in_progress': 'Checking the Clarity guides...',
  'response.file_search_call.searching': 'Checking the Clarity guides...',
  'response.web_search_call.in_progress': 'Searching the web...',
  'response.web_search_call.searching': 'Searching the web...',
  'response.reasoning.delta': 'Thinking...',
};

const TERMINAL_OK_EVENTS = new Set(['response.completed', 'response.incomplete']);
const TERMINAL_ERROR_EVENTS = new Set(['response.failed', 'response.cancelled', 'error']);

const readErrorMessage = async (response, fallback) => {
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      return data.error?.message || data.error || data.message || fallback;
    }
    const text = await response.text();
    if (!text) return fallback;
    try {
      const data = JSON.parse(text);
      return data.error?.message || data.error || data.message || fallback;
    } catch {
      return text;
    }
  } catch {
    return fallback;
  }
};

export const EsterService = {
  getConfig: () => ChatbotConfig,

  getAuthToken: () => {
    const sources = [
      () => Cookies.get('token') || Cookies.get('jwt') || Cookies.get('access_token'),
      () => localStorage.getItem('token') || localStorage.getItem('jwt') || localStorage.getItem('access_token'),
      () => sessionStorage.getItem('token') || sessionStorage.getItem('jwt') || sessionStorage.getItem('access_token'),
    ];
    for (const read of sources) {
      try {
        const token = read();
        if (token) return token;
      } catch {
      }
    }
    return null;
  },

  getHeaders: () => {
    const token = EsterService.getAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  },

  createConversation: async () => {
    const config = EsterService.getConfig();
    const response = await fetch(`${config.api.baseUrl}/conversations`, {
      method: 'POST',
      headers: EsterService.getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      const fallback = `Backend API error: ${response.status} ${response.statusText}`;
      throw new Error(
        response.status === 401
          ? 'Authentication failed (401). Please log in again.'
          : await readErrorMessage(response, fallback)
      );
    }

    const data = await response.json();
    if (!data?.conversationId) {
      throw new Error('Backend did not return a conversationId.');
    }

    return {
      conversationId: data.conversationId,
      createdAt: data.createdAt || new Date().toISOString(),
    };
  },

  sendMessageStream: async (conversationId, message, { onChunk, onComplete, onError, onStatus, signal }) => {
    const config = EsterService.getConfig();

    if (!conversationId) {
      onError(new Error('Invalid conversation ID. Conversation may not have been initialised.'));
      return;
    }

    let finished = false;
    const complete = () => {
      if (finished) return;
      finished = true;
      onComplete();
    };
    const fail = (err) => {
      if (finished) return;
      finished = true;
      onError(err);
    };

    try {
      const response = await fetch(
        `${config.api.baseUrl}/conversations/${encodeURIComponent(conversationId)}/messages`,
        {
          method: 'POST',
          headers: { ...EsterService.getHeaders(), Accept: 'text/event-stream' },
          credentials: 'include',
          body: JSON.stringify({ message }),
          signal,
        }
      );

      if (!response.ok) {
        const fallback = `Failed to send message: ${response.status} ${response.statusText}`;
        throw new Error(await readErrorMessage(response, fallback));
      }
      if (!response.body) {
        throw new Error('Response body is not readable. Server may not support streaming.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = null;

      const handleEvent = (eventName, data) => {
        if (data === '[DONE]') {
          complete();
          return true;
        }

        let parsed;
        try {
          parsed = JSON.parse(data);
        } catch {
          return false;
        }

        const type = eventName || parsed.type;

        if (type === 'response.output_text.delta') {
          if (typeof parsed.delta === 'string' && parsed.delta) onChunk(parsed.delta);
          return false;
        }

        // Progress fillers shown while waiting for the first token.
        if (onStatus && type && STATUS_BY_EVENT[type]) {
          onStatus(STATUS_BY_EVENT[type]);
          return false;
        }

        if (TERMINAL_OK_EVENTS.has(type)) {
          complete();
          return true;
        }

        if (TERMINAL_ERROR_EVENTS.has(type)) {
          const detail =
            parsed.error?.message ||
            parsed.response?.error?.message ||
            parsed.message ||
            (type === 'response.cancelled' ? 'Run was cancelled' : 'Run failed');
          fail(new Error(detail));
          return true;
        }

        return false;
      };

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            complete();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split(/\r?\n/);
          buffer = lines.pop() || '';

          let stop = false;
          for (const rawLine of lines) {
            const line = rawLine.trim();
            if (line === '') {
              currentEvent = null;
              continue;
            }
            if (line.startsWith('event:')) {
              currentEvent = line.slice(6).trim();
              continue;
            }
            if (line.startsWith('data:')) {
              stop = handleEvent(currentEvent, line.slice(5).trim());
              if (stop) break;
            }
          }
          if (stop) break;
        }
      } finally {
        try {
          reader.releaseLock();
        } catch {
          
        }
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        complete();
        return;
      }
      console.error('[Ester] Stream error:', error);
      fail(error);
    }
  },

  getConversationMessages: async (conversationId, limit = 20) => {
    const config = EsterService.getConfig();
    const response = await fetch(
      `${config.api.baseUrl}/conversations/${encodeURIComponent(conversationId)}/messages?limit=${limit}`,
      { method: 'GET', headers: EsterService.getHeaders(), credentials: 'include' }
    );
    if (!response.ok) throw new Error('Failed to retrieve messages');
    const data = await response.json();
    return Array.isArray(data) ? data : data.data || [];
  },

  stripCitationMarkers: (text) => {
    if (!text) return '';
    return text
      .replace(/[\uE000-\uF8FF]+(?:filecite|cite|turn\d+(?:file|search|view)\d+)[^\uE000-\uF8FF]*?[\uE000-\uF8FF]+/g, '')
      .replace(/[\uE000-\uF8FF]/g, '')
      .replace(/\s*(?:filecite|turn\d+(?:file|search|view)\d+)\b/g, '')
      .replace(/[ \t]+\n/g, '\n');
  },

  markdownToPlainText: (md) => {
    if (!md) return '';
    const out = [];
    for (let line of md.split('\n')) {
      if (line.includes('-') && /^\s*\|?[\s:|-]+\|?\s*$/.test(line)) continue;
      if (/^\s*\|.*\|\s*$/.test(line)) {
        const cells = line.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
        line = cells.join(' | ');
      }
      line = line.replace(/^#{1,6}\s+/, '');
      out.push(line);
    }
    return out
      .join('\n')
      .replace(/<br\s*\/?>/gi, '; ')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/^(\s*)\*\s+/gm, '$1- ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  },

  validateConfig: () => {
    const token = EsterService.getAuthToken();
    let user = null;
    try {
      user = sessionStorage.getItem('user');
    } catch {
      // ignore
    }

    if (!user && !token) {
      return {
        valid: false,
        message: 'Authentication token not found. Please log in to use the chat feature.',
      };
    }
    return { valid: true, message: 'Configuration is valid.' };
  },
};

export default EsterService;
